import { CheckIcon } from '@heroicons/react/24/outline';
import React from 'react';

import { cn } from '@/lib/utils';

import type { WizardStepperProps, WizardStepState } from './types';

/**
 * Canonical visual step header for multi-step wizards.
 *
 * Pure presentational component: it holds no state and reads no hooks. It takes a
 * plain, already-ordered+visible `steps` array plus a 1-based `currentStep`, so it
 * works for wizards that use `useWizard` AND wizards that hand-roll their own step
 * state (Cloud, Hannibal). For `useWizard` callers, pass the visible (skip-filtered)
 * steps — already sorted by `order` — and `progress.currentDisplayStep` as
 * `currentStep`. Never derive `currentStep` from `progress.percentage` (that value is
 * validation-aware, not a step position). `isFinal`/`order` are ignored here.
 *
 * Two variants:
 * - `dots` (default): compact progress-fill dots. Use for compact/unlabeled or many steps.
 * - `rail`: numbered circles + labels with connectors. Use for <=4 labeled steps.
 *
 * React 19 + React Compiler: do NOT add useMemo/useCallback. `stateFor` is a pure
 * inline function.
 */
const stateFor = (n: number, currentStep: number): WizardStepState =>
  n < currentStep ? 'completed' : n === currentStep ? 'current' : 'upcoming';

export const WizardStepper: React.FC<WizardStepperProps> = ({
  steps,
  currentStep,
  variant = 'dots',
  onStepClick,
  className,
  ariaLabel = 'Progress',
}) => {
  const total = steps.length;
  const currentTitle = steps[currentStep - 1]?.title ?? '';

  if (variant === 'dots') {
    return (
      <div
        role="group"
        aria-label={ariaLabel}
        className={cn('flex min-w-0 items-center', className)}
      >
        {/* Dots are decorative; the live region below announces progress.
            `list-none` is required to suppress the default <ol> "1." "2."
            markers (no global list reset is applied here). */}
        <ol
          aria-hidden="true"
          className="flex min-w-0 list-none items-center gap-1.5"
        >
          {steps.map((step, index) => {
            const n = index + 1;
            const filled = n <= currentStep;
            return (
              <li
                key={step.id}
                className={cn(
                  'size-1.5 shrink-0 rounded-full transition-colors',
                  filled ? 'bg-chariot-brand-primary' : 'bg-chariot-stroke'
                )}
              />
            );
          })}
        </ol>
        {currentTitle && (
          <span
            aria-hidden="true"
            className="text-chariot-text-secondary ml-2 min-w-0 truncate text-xs"
          >
            {currentTitle}
          </span>
        )}
        <span className="sr-only" aria-live="polite">
          {`Step ${currentStep} of ${total}: ${currentTitle}`}
        </span>
      </div>
    );
  }

  // variant === 'rail'
  return (
    <div
      role="group"
      aria-label={ariaLabel}
      className={cn('flex min-w-0 items-center', className)}
    >
      <ol className="flex min-w-0 list-none items-center">
        {steps.map((step, index) => {
          const n = index + 1;
          const state = stateFor(n, currentStep);
          const isCompleted = state === 'completed';
          const isCurrent = state === 'current';
          const isClickable = isCompleted && Boolean(onStepClick);

          const accessibleName = `Step ${n}: ${step.title} (${state})`;

          const circle = (
            <span
              className={cn(
                'flex size-[22px] shrink-0 items-center justify-center rounded-full border-[1.5px] text-xs font-bold',
                isCurrent &&
                  'border-chariot-brand-primary bg-chariot-brand-primary text-primary-foreground',
                isCompleted && 'border-success text-success',
                state === 'upcoming' &&
                  'border-chariot-stroke text-chariot-text-secondary'
              )}
            >
              {isCompleted ? <CheckIcon className="size-3" /> : n}
            </span>
          );

          const label = (
            <span
              className={cn(
                'truncate text-sm font-medium',
                isCurrent
                  ? 'text-chariot-text-primary'
                  : 'text-chariot-text-secondary'
              )}
            >
              {step.title}
            </span>
          );

          const stepContent = isClickable ? (
            <button
              type="button"
              onClick={() => onStepClick?.(n)}
              aria-label={accessibleName}
              className="focus-visible:ring-chariot-brand-primary flex min-w-0 items-center gap-2 rounded-md outline-hidden focus-visible:ring-2"
            >
              {circle}
              {label}
            </button>
          ) : (
            // Non-interactive step: the accessible name lives on the <li>
            // below. A bare <span> has implicit role="generic", which is
            // name-prohibited (WAI-ARIA 1.2 §5.2.8.6) — an aria-label here is
            // silently dropped by assistive tech, muting the spoken state.
            <span className="flex min-w-0 items-center gap-2">
              {circle}
              {label}
            </span>
          );

          return (
            <li
              key={step.id}
              className={cn('flex min-w-0 items-center', index > 0 && 'flex-1')}
              aria-current={isCurrent ? 'step' : undefined}
              // Clickable steps take their accessible name from the inner
              // <button>; repeating it on the <li> makes screen readers announce
              // the name twice. Non-interactive steps still need it here — their
              // only child is a bare <span> (role="generic", name-prohibited).
              aria-label={isClickable ? undefined : accessibleName}
            >
              {/* Connector precedes the step and lives INSIDE the <li> (an <ol>
                  may only contain <li> + script-supporting elements; a sibling
                  <span> is invalid). aria-hidden, so it never affects the list
                  count or SR output. Color reflects whether the step BEFORE the
                  connector is completed. */}
              {index > 0 && (
                <span
                  aria-hidden="true"
                  className={cn(
                    'mx-3 h-0.5 min-w-6 flex-1',
                    stateFor(n - 1, currentStep) === 'completed'
                      ? 'bg-success'
                      : 'bg-chariot-stroke'
                  )}
                />
              )}
              {stepContent}
            </li>
          );
        })}
      </ol>
    </div>
  );
};
